import { useYoutubeCampaigns } from './useYoutubeCampaigns';
import YoutubePublicReportCampaignsTable from '../CampaignsTable/YoutubePublicReportCampaignTable';
import useUrlState from 'Hooks/useUrlState';
import { useMemo } from 'react';
import { ExpandedState } from '@tanstack/react-table';
import PublicReportYoutubePostsTable from '../../Posts/YoutubePosts/PublicReportYoutubePostsTable';
import { useCopyReportCampaignPostUrls } from 'Modules/Plans/Reports/Report/hooks/useCopyReportCampaignPostUrls';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable/Button/PostUrlClipboardButton';
import styles from '../PublicReportCampaigns.module.css';

type Props = {
    publicReportId: string | undefined;
};
type UrlState = Partial<{
    expanded: string;
}>;

const ReportYoutubeCampaigns = ({ publicReportId }: Props) => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const {
        rows,
        isCampaignsLoading,
        isCampaignsError,
        isProjectsLoading,
        isBrandLoading,
        isTeamsLoading,
        isCampaignStatsLoading,
    } = useYoutubeCampaigns();

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }
        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const { isLoading: isCopyingPostUrls, copyReportCampaignPostUrls } = useCopyReportCampaignPostUrls(
        'youtube',
        rows,
        publicReportId
    );

    return (
        <>
            <div className={styles.actions}>
                <PostUrlClipboardButton
                    onClick={() => copyReportCampaignPostUrls()}
                    disabled={!rows.length}
                    isLoading={isCopyingPostUrls}
                />
            </div>
            <YoutubePublicReportCampaignsTable
                data={rows}
                meta={{
                    isLoading: isCampaignsLoading,
                    getIsProjectLoading: () => isProjectsLoading,
                    getIsTeamLoading: () => isTeamsLoading,
                    getIsBrandLoading: (row) => isBrandLoading(row.id),
                    getIsCampaignStatsLoading: (row) => isCampaignStatsLoading(row.id),
                }}
                isLoading={isCampaignsLoading}
                hasError={isCampaignsError}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => <PublicReportYoutubePostsTable campaign={row.original} />}
            />
        </>
    );
};

export default ReportYoutubeCampaigns;
